import { useEffect, useMemo, useRef, useState } from 'react';

import { useOneTrustContext } from '@/utils/onetrust-provider';

import { CstStaticFormEmbed } from './cst-static-form-embed';
import {
	parseThirdPartyEmbedHtml,
	type ParsedThirdPartyEmbed,
} from './parse-third-party-embed-html';

const OUTGROW_LOADER_URL = 'https://dyv6f9ner1ir9.cloudfront.net/assets/js/nloader.js';
const SUPPORT_FORM_PRODUCTION_HOST =
	'https://support-form-app-ui.prod-east.frontend.public.atl-paas.net';
const SUPPORT_FORM_STAGING_HOST =
	'https://support-form-app-ui.stg-east.frontend.public.atl-paas.net';
const SUPPORT_FORM_MANIFEST_PATH = '/assets/asset-manifest.json';
const SUPPORT_FORM_APP_TARGET_ID = 'support-form-app';
const SUPPORT_FORM_SCOPE_SELECTOR = '[data-third-party-embed-scope="supportForm"]';
const ECOSYSTEMS_IFRAME_ORIGIN = 'https://atlassian.wvc-test.ecosystems.io';
const ECOSYSTEMS_IFRAME_ID = 'webapp-frame';
const MAX_IFRAME_HEIGHT_PX = 100_000;
const SCRIPT_LOAD_TIMEOUT_MS = 15_000;

interface LoadingScript {
	promise: Promise<void>;
	script: HTMLScriptElement;
}

interface SupportFormAssets {
	scriptUrl: string;
	stylesheetUrl: string;
}

const loadingScripts = new Map<string, LoadingScript>();

declare global {
	interface Window {
		initIframe?: (embedId: string) => void;
	}
}

export interface UniformThirdPartyEmbedProps {
	name: string;
	rawHtml: string;
}

const findScript = (url: string): HTMLScriptElement | undefined =>
	Array.from(document.scripts).find((script) => script.src === url);

const loadScript = (url: string, isReady: () => boolean): Promise<void> => {
	if (isReady()) return Promise.resolve();

	const cachedScript = loadingScripts.get(url);
	if (cachedScript?.script.isConnected) return cachedScript.promise;
	loadingScripts.delete(url);

	findScript(url)?.remove();
	const script = document.createElement('script');
	script.src = url;
	script.async = true;
	script.defer = true;
	script.dataset['thirdPartyEmbedScript'] = 'true';

	const promise = new Promise<void>((resolve, reject) => {
		const cleanupListeners = () => {
			clearTimeout(timeoutId);
			script.removeEventListener('load', handleLoad);
			script.removeEventListener('error', handleError);
		};
		const handleLoad = () => {
			cleanupListeners();
			if (isReady()) resolve();
			else reject(new Error('Provider global was unavailable after script load'));
		};
		const handleError = () => {
			cleanupListeners();
			reject(new Error('Provider script failed to load'));
		};
		const timeoutId = window.setTimeout(() => {
			cleanupListeners();
			reject(new Error('Provider script load timed out'));
		}, SCRIPT_LOAD_TIMEOUT_MS);

		script.addEventListener('load', handleLoad);
		script.addEventListener('error', handleError);
	});

	const loadingScript = { promise, script };
	loadingScripts.set(url, loadingScript);
	void promise.then(
		() => {
			if (loadingScripts.get(url) === loadingScript) loadingScripts.delete(url);
		},
		() => {
			if (loadingScripts.get(url) === loadingScript) loadingScripts.delete(url);
			script.remove();
		},
	);

	document.head.appendChild(script);
	return promise;
};

const logFailure = (provider: ParsedThirdPartyEmbed['provider'] | 'unknown', phase: string) => {
	console.error(`[thirdPartyEmbed] ${provider} ${phase} failed`);
};

const getSupportFormHost = (): string =>
	window.location.hostname === 'www.atlassian.com'
		? SUPPORT_FORM_PRODUCTION_HOST
		: SUPPORT_FORM_STAGING_HOST;

const getApprovedSupportAssetUrl = (
	value: unknown,
	host: string,
	extension: '.js' | '.css',
): string => {
	if (typeof value !== 'string') throw new Error('Support-form manifest asset is missing');

	const url = new URL(value);
	const approvedHost = new URL(host);
	if (
		url.origin !== approvedHost.origin ||
		url.username !== '' ||
		url.password !== '' ||
		!url.pathname.startsWith('/assets/') ||
		!url.pathname.endsWith(extension)
	) {
		throw new Error('Support-form manifest asset is not approved');
	}

	return url.href;
};

const loadSupportFormAssets = async (
	host: string,
	scriptKey: string,
	stylesheetKey: string,
	signal: AbortSignal,
): Promise<SupportFormAssets> => {
	const response = await fetch(`${host}${SUPPORT_FORM_MANIFEST_PATH}`, {
		credentials: 'include',
		signal,
	});
	if (!response.ok) throw new Error('Support-form manifest failed to load');

	const manifest: unknown = await response.json();
	if (typeof manifest !== 'object' || manifest === null || Array.isArray(manifest)) {
		throw new Error('Support-form manifest is invalid');
	}

	const entries = manifest as Record<string, unknown>;
	return {
		scriptUrl: getApprovedSupportAssetUrl(entries[scriptKey], host, '.js'),
		stylesheetUrl: getApprovedSupportAssetUrl(entries[stylesheetKey], host, '.css'),
	};
};

const waitForElementLoad = (
	element: HTMLLinkElement | HTMLScriptElement,
	signal: AbortSignal,
): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal.aborted) {
			reject(new Error('Support-form initialization was cancelled'));
			return;
		}

		const cleanup = () => {
			clearTimeout(timeoutId);
			element.removeEventListener('load', handleLoad);
			element.removeEventListener('error', handleError);
			signal.removeEventListener('abort', handleAbort);
		};
		const handleLoad = () => {
			cleanup();
			resolve();
		};
		const handleError = () => {
			cleanup();
			reject(new Error('Support-form asset failed to load'));
		};
		const handleAbort = () => {
			cleanup();
			reject(new Error('Support-form initialization was cancelled'));
		};
		const timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error('Support-form asset load timed out'));
		}, SCRIPT_LOAD_TIMEOUT_MS);

		element.addEventListener('load', handleLoad);
		element.addEventListener('error', handleError);
		signal.addEventListener('abort', handleAbort, { once: true });
	});

const waitForSupportFormRender = (target: HTMLDivElement, signal: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal.aborted) {
			reject(new Error('Support-form initialization was cancelled'));
			return;
		}

		if (target.childElementCount > 0) {
			resolve();
			return;
		}

		const cleanup = () => {
			clearTimeout(timeoutId);
			observer.disconnect();
			signal.removeEventListener('abort', handleAbort);
		};
		const handleAbort = () => {
			cleanup();
			reject(new Error('Support-form initialization was cancelled'));
		};
		const observer = new MutationObserver(() => {
			if (target.childElementCount === 0) return;
			cleanup();
			resolve();
		});
		const timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error('Support form did not render'));
		}, SCRIPT_LOAD_TIMEOUT_MS);

		observer.observe(target, { childList: true });
		signal.addEventListener('abort', handleAbort, { once: true });
	});

const splitSelectorList = (selectorList: string): string[] => {
	const selectors: string[] = [];
	let bracketDepth = 0;
	let parenthesisDepth = 0;
	let start = 0;

	for (let index = 0; index < selectorList.length; index += 1) {
		const character = selectorList[index];
		if (character === '[') bracketDepth += 1;
		else if (character === ']') bracketDepth -= 1;
		else if (character === '(') parenthesisDepth += 1;
		else if (character === ')') parenthesisDepth -= 1;
		else if (character === ',' && bracketDepth === 0 && parenthesisDepth === 0) {
			selectors.push(selectorList.slice(start, index).trim());
			start = index + 1;
		}
	}

	selectors.push(selectorList.slice(start).trim());
	return selectors;
};

const scopeSupportFormRules = (rules: CSSRuleList, isInsideKeyframes = false) => {
	for (const rule of Array.from(rules)) {
		if (rule instanceof CSSKeyframesRule) {
			scopeSupportFormRules(rule.cssRules, true);
			continue;
		}
		if (rule instanceof CSSStyleRule && !isInsideKeyframes) {
			rule.selectorText = splitSelectorList(rule.selectorText)
				.map((selector) => `:where(${SUPPORT_FORM_SCOPE_SELECTOR}) ${selector}`)
				.join(', ');
			continue;
		}
		if ('cssRules' in rule && rule.cssRules instanceof CSSRuleList) {
			scopeSupportFormRules(rule.cssRules, isInsideKeyframes);
		}
	}
};

const loadScopedSupportFormStylesheet = async (
	stylesheetUrl: string,
	signal: AbortSignal,
): Promise<CSSStyleSheet> => {
	const response = await fetch(stylesheetUrl, { credentials: 'include', signal });
	if (!response.ok) throw new Error('Support-form stylesheet failed to load');

	const stylesheet = new CSSStyleSheet();
	stylesheet.replaceSync(await response.text());
	scopeSupportFormRules(stylesheet.cssRules);
	return stylesheet;
};

const keepSupportFormHeightInSync = (appTarget: HTMLDivElement): (() => void) => {
	let minimumHeight = 0;
	const updateMinimumHeight = () => {
		const contentHeight = Math.ceil(appTarget.scrollHeight);
		if (contentHeight <= minimumHeight) return;

		minimumHeight = contentHeight;
		appTarget.style.minHeight = `${minimumHeight}px`;
	};

	const mutationObserver = new MutationObserver(updateMinimumHeight);
	mutationObserver.observe(appTarget, {
		attributes: true,
		childList: true,
		characterData: true,
		subtree: true,
	});

	const resizeObserver =
		typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateMinimumHeight);
	resizeObserver?.observe(appTarget);
	window.addEventListener('resize', updateMinimumHeight);
	updateMinimumHeight();

	return () => {
		mutationObserver.disconnect();
		resizeObserver?.disconnect();
		window.removeEventListener('resize', updateMinimumHeight);
		appTarget.style.removeProperty('min-height');
	};
};

const initializeSupportForm = async (
	host: HTMLDivElement,
	scriptKey: string,
	stylesheetKey: string,
	signal: AbortSignal,
): Promise<() => void> => {
	if (document.querySelectorAll('[data-third-party-embed-provider="supportForm"]').length !== 1) {
		throw new Error('Support form must be unique');
	}

	host.replaceChildren();

	const appTarget = document.createElement('div');
	appTarget.id = SUPPORT_FORM_APP_TARGET_ID;
	host.appendChild(appTarget);

	const assets = await loadSupportFormAssets(
		getSupportFormHost(),
		scriptKey,
		stylesheetKey,
		signal,
	);
	if (signal.aborted) throw new Error('Support-form initialization was cancelled');

	const script = document.createElement('script');
	script.src = assets.scriptUrl;
	script.async = true;
	script.dataset['thirdPartyEmbedScript'] = 'true';

	const stylesheet = await loadScopedSupportFormStylesheet(assets.stylesheetUrl, signal);
	document.adoptedStyleSheets = [...document.adoptedStyleSheets, stylesheet];
	const scriptLoaded = waitForElementLoad(script, signal);
	const formRendered = waitForSupportFormRender(appTarget, signal);
	document.head.appendChild(script);

	try {
		await Promise.all([scriptLoaded, formRendered]);
		if (signal.aborted) throw new Error('Support-form initialization was cancelled');
		const cleanupHeightSynchronization = keepSupportFormHeightInSync(appTarget);
		return () => {
			cleanupHeightSynchronization();
			document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
				(adoptedStylesheet) => adoptedStylesheet !== stylesheet,
			);
		};
	} catch (error) {
		document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
			(adoptedStylesheet) => adoptedStylesheet !== stylesheet,
		);
		throw error;
	}
};

const getEmbedKey = (embed: ParsedThirdPartyEmbed): string => {
	if (embed.provider === 'cstStaticForm') {
		return `${embed.provider}:${embed.formName}:${embed.height}`;
	}
	if (embed.provider === 'outgrow') {
		return `${embed.provider}:${embed.embedId}:${embed.embedUrl}:${embed.title}`;
	}
	if (embed.provider === 'supportForm') {
		return `${embed.provider}:${embed.scriptKey}:${embed.stylesheetKey}`;
	}
	return `${embed.provider}:${embed.embedUrl}`;
};

const initializeOutgrow = async (
	embed: Extract<ParsedThirdPartyEmbed, { provider: 'outgrow' }>,
) => {
	if (document.querySelectorAll(`[id="${embed.embedId}"]`).length !== 1) {
		throw new Error('Outgrow embed ID must be unique');
	}

	await loadScript(OUTGROW_LOADER_URL, () => typeof window.initIframe === 'function');
	window.initIframe?.(embed.embedId);
};

const initializeEcosystemsIframe = (iframe: HTMLIFrameElement): (() => void) => {
	if (document.querySelectorAll(`[id="${ECOSYSTEMS_IFRAME_ID}"]`).length !== 1) {
		throw new Error('Ecosystems iframe ID must be unique');
	}

	const handleResizeMessage = (event: MessageEvent) => {
		if (event.origin !== ECOSYSTEMS_IFRAME_ORIGIN || event.source !== iframe.contentWindow) return;
		if (typeof event.data !== 'object' || event.data === null) return;
		if (event.data.type !== 'resize-iframe') return;

		const height = event.data.height;
		if (typeof height !== 'number' || !Number.isFinite(height)) return;
		if (height <= 0 || height > MAX_IFRAME_HEIGHT_PX) return;
		iframe.style.height = `${height}px`;
	};

	window.addEventListener('message', handleResizeMessage);
	return () => window.removeEventListener('message', handleResizeMessage);
};

export const UniformThirdPartyEmbed = ({ rawHtml }: UniformThirdPartyEmbedProps) => {
	const parsedEmbed = useMemo(() => parseThirdPartyEmbedHtml(rawHtml), [rawHtml]);
	const { analyticsOptOut, isConsentLoaded } = useOneTrustContext();
	const mountRef = useRef<HTMLDivElement>(null);
	const supportFormHostRef = useRef<HTMLDivElement>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const embedKey = parsedEmbed ? getEmbedKey(parsedEmbed) : undefined;
	const [failure, setFailure] = useState<{ embedKey: string } | null>(null);

	const requiresPerformanceConsent =
		parsedEmbed?.provider === 'outgrow' || parsedEmbed?.provider === 'ecosystemsIframe';
	const hasConsent = !requiresPerformanceConsent || (isConsentLoaded && !analyticsOptOut);
	const shouldRender = parsedEmbed !== null && hasConsent && failure?.embedKey !== embedKey;

	useEffect(() => {
		if (parsedEmbed !== null) return;
		logFailure('unknown', 'parse');
	}, [parsedEmbed]);

	useEffect(() => {
		if (!shouldRender || !parsedEmbed) return;
		if (parsedEmbed.provider === 'cstStaticForm') return;

		if (parsedEmbed.provider === 'ecosystemsIframe') {
			if (!iframeRef.current) return;
			try {
				return initializeEcosystemsIframe(iframeRef.current);
			} catch {
				logFailure(parsedEmbed.provider, 'initialize');
				setFailure({ embedKey: getEmbedKey(parsedEmbed) });
				return;
			}
		}

		if (parsedEmbed.provider === 'supportForm') {
			if (!supportFormHostRef.current) return;

			let active = true;
			const host = supportFormHostRef.current;
			const abortController = new AbortController();
			let cleanupSupportForm = () => {};
			initializeSupportForm(
				host,
				parsedEmbed.scriptKey,
				parsedEmbed.stylesheetKey,
				abortController.signal,
			).then(
				(cleanup) => {
					if (active) cleanupSupportForm = cleanup;
					else cleanup();
				},
				() => {
					if (!active) return;
					host.replaceChildren();
					logFailure(parsedEmbed.provider, 'initialize');
					setFailure({ embedKey: getEmbedKey(parsedEmbed) });
				},
			);

			return () => {
				active = false;
				abortController.abort();
				cleanupSupportForm();
				document
					.querySelectorAll(
						'script[data-third-party-embed-script="true"][src*="support-form-app-ui"]',
					)
					.forEach((script) => script.remove());
				host.replaceChildren();
			};
		}

		if (!mountRef.current) return;

		let active = true;
		const initialize = initializeOutgrow(parsedEmbed);

		initialize.catch(() => {
			if (!active) return;
			logFailure(parsedEmbed.provider, 'initialize');
			setFailure({ embedKey: getEmbedKey(parsedEmbed) });
		});

		return () => {
			active = false;
			mountRef.current?.replaceChildren();
		};
	}, [parsedEmbed, shouldRender]);

	if (!shouldRender || !parsedEmbed) return null;

	if (parsedEmbed.provider === 'cstStaticForm') {
		return (
			<CstStaticFormEmbed
				key={embedKey}
				formName={parsedEmbed.formName}
				height={parsedEmbed.height}
			/>
		);
	}

	if (parsedEmbed.provider === 'outgrow') {
		return (
			<div
				ref={mountRef}
				className="op-interactive"
				id={parsedEmbed.embedId}
				data-title={parsedEmbed.title}
				data-url={parsedEmbed.embedUrl}
				data-width="100%"
				data-third-party-embed-provider="outgrow"
			/>
		);
	}

	if (parsedEmbed.provider === 'ecosystemsIframe') {
		return (
			<iframe
				ref={iframeRef}
				id={ECOSYSTEMS_IFRAME_ID}
				src={parsedEmbed.embedUrl}
				title="Atlassian web application"
				style={{ width: '100%', borderWidth: 0, overflow: 'hidden' }}
				scrolling="no"
				data-third-party-embed-provider="ecosystemsIframe"
				onError={() => {
					logFailure(parsedEmbed.provider, 'load');
					setFailure({ embedKey: getEmbedKey(parsedEmbed) });
				}}
			/>
		);
	}

	return (
		<div
			ref={supportFormHostRef}
			data-third-party-embed-provider="supportForm"
			data-third-party-embed-scope="supportForm"
		/>
	);
};

export default UniformThirdPartyEmbed;
